import {
  type IrohAddress,
  type IrohBinder,
  type IrohEndpoint,
  isEndpointId,
  isRelayUrl,
} from "@codeshare/iroh";
import type { SignalingAdapter, SignalPayload, Unsubscribe } from "@codeshare/signaling";
import { IrohPeer } from "./iroh-peer";
import { Peer, type PeerOptions } from "./peer";

/** A connection to one peer: direct (WebRTC), or through an iroh relay when that fails. */
export type MeshPeer = Peer | IrohPeer;

export interface MeshOptions {
  selfId: string;
  /** Room rendezvous (from deriveRoomKeys). */
  topic: string;
  sigKey: CryptoKey;
  /** One or more signaling media; peers found on any are de-duplicated by id. */
  adapters: SignalingAdapter[];
  rtcConfig?: RTCConfiguration;
  /**
   * Relays to fall back to when a direct connection to a peer that's there fails. Without this,
   * such peers stay unreachable (see `unreachable`).
   */
  iroh?: { relayUrls: string[]; bind: IrohBinder };
  /** Full-mesh size limit (docs/PRD.md §14). Extra peers are ignored. */
  maxPeers?: number;
  /** Extra Peer options (tests: timeouts, RTCPeerConnection implementation). */
  peerOptions?: Partial<
    Pick<
      PeerOptions,
      "gatherTimeoutMs" | "restartDelayMs" | "connectTimeoutMs" | "RTCPeerConnection"
    >
  >;
  /** Waits before trying a peer again after its 1st, 2nd, 3rd… failed attempt (last repeats). */
  retryDelaysMs?: readonly number[];
  /** After a retry, a failed peer that isn't heard from this long is taken to be gone. */
  goneAfterMs?: number;
}

const RETRY_DELAYS_MS = [5_000, 15_000, 45_000];
/** Loading iroh and reaching a relay should take seconds; past this, no relay is reachable. */
const BIND_TIMEOUT_MS = 30_000;
/** Longer than the signaling's steady announce interval, so a live peer is heard from first. */
const GONE_AFTER_MS = 45_000;

export type LeaveReason = "closed" | "failed";

/**
 * A full mesh of peer connections for one room: discovers peers through signaling, keeps one
 * peer connection per peer id, and drops peers whose connection closes or fails (they can
 * rediscover us).
 *
 * Relay fallback: when a direct connection to a peer that answered fails, both sides switch to
 * an iroh relay for that peer. Each tells the other its endpoint address with a `relay` signal;
 * the impolite side dials, the polite side accepts. A side that hears `relay` first follows.
 */
export class PeerMesh {
  readonly selfId: string;
  readonly #options: MeshOptions & { maxPeers: number };
  readonly #peers = new Map<string, MeshPeer>();
  /** Which adapter to use when signaling each peer (the first that found it). */
  readonly #routes = new Map<string, SignalingAdapter>();
  readonly #peerListeners = new Set<(peer: MeshPeer) => void>();
  readonly #leftListeners = new Set<(peerId: string, reason: LeaveReason) => void>();
  readonly #rejectedListeners = new Set<(peerId: string) => void>();
  readonly #fullListeners = new Set<(peerId: string, maxPeers: number) => void>();
  readonly #unreachableListeners = new Set<() => void>();
  readonly #rejected = new Set<string>();
  readonly #blocked = new Set<string>();
  /**
   * Peers whose connections failed, and when to try them again. Until then, `timer` retries; after
   * it, `timer` forgets the peer unless it's heard from (a closed tab never is).
   */
  readonly #failures = new Map<
    string,
    { count: number; answered: number; retryAt: number; timer: ReturnType<typeof setTimeout> }
  >();
  /** Peers reached through a relay from now on (a direct connection to them failed). */
  readonly #relayed = new Set<string>();
  /** How to reach peers through their relays, as they told us. */
  readonly #addresses = new Map<string, IrohAddress>();
  #endpoint: Promise<IrohEndpoint> | undefined;
  #subscriptions: Unsubscribe[] = [];
  #running = false;

  constructor(options: MeshOptions) {
    this.selfId = options.selfId;
    this.#options = { maxPeers: 8, ...options };
  }

  get peers(): ReadonlyMap<string, MeshPeer> {
    return this.#peers;
  }

  /**
   * Peers that answered but still failed to connect, more than once: they're there, but the
   * networks between block direct connections (and the relay fallback didn't help, or isn't
   * configured). Peers that never answer are likely gone (closed tabs whose announcements
   * relays still pass on) and aren't counted.
   */
  get unreachable(): string[] {
    return [...this.#failures].filter(([, f]) => f.answered >= 2).map(([id]) => id);
  }

  /** The list of unreachable peers changed. */
  onUnreachableChange(listener: () => void): Unsubscribe {
    this.#unreachableListeners.add(listener);
    return () => this.#unreachableListeners.delete(listener);
  }

  /** Called for every new peer connection (before it's connected). */
  onPeer(listener: (peer: MeshPeer) => void): Unsubscribe {
    this.#peerListeners.add(listener);
    return () => this.#peerListeners.delete(listener);
  }

  onPeerLeft(listener: (peerId: string, reason: LeaveReason) => void): Unsubscribe {
    this.#leftListeners.add(listener);
    return () => this.#leftListeners.delete(listener);
  }

  /** A peer turned us away because it's connected to as many people as it allows. */
  onFull(listener: (peerId: string, maxPeers: number) => void): Unsubscribe {
    this.#fullListeners.add(listener);
    return () => this.#fullListeners.delete(listener);
  }

  /** A peer was ignored because the room is full. */
  onRejected(listener: (peerId: string) => void): Unsubscribe {
    this.#rejectedListeners.add(listener);
    return () => this.#rejectedListeners.delete(listener);
  }

  /**
   * Disconnects a peer and never reconnects to it (e.g. it failed authentication). Peer ids are
   * per session, so this doesn't block the same person rejoining later.
   */
  block(peerId: string): void {
    this.#blocked.add(peerId);
    const peer = this.#peers.get(peerId);
    this.#peers.delete(peerId);
    peer?.close();
  }

  async start(): Promise<void> {
    if (this.#running) return;
    this.#running = true;
    const { selfId, topic, sigKey, adapters } = this.#options;
    for (const adapter of adapters) {
      this.#subscriptions.push(
        adapter.onPeerSeen((peerId) => this.#discovered(peerId, adapter)),
        adapter.onSignal((from, payload) => void this.#signal(from, payload, adapter)),
      );
    }
    // Any adapter joining is enough; individual failures just reduce redundancy.
    const results = await Promise.allSettled(
      adapters.map((a) => a.join({ topic, sigKey, selfId })),
    );
    if (results.every((r) => r.status === "rejected")) {
      await this.stop();
      throw new Error("could not join any signaling");
    }
  }

  async stop(): Promise<void> {
    this.#running = false;
    for (const unsubscribe of this.#subscriptions.splice(0)) unsubscribe();
    for (const peer of [...this.#peers.values()]) peer.close();
    this.#peers.clear();
    for (const failure of this.#failures.values()) clearTimeout(failure.timer);
    this.#failures.clear();
    this.#routes.clear();
    this.#relayed.clear();
    this.#addresses.clear();
    const endpoint = this.#endpoint;
    this.#endpoint = undefined;
    await Promise.allSettled([
      ...this.#options.adapters.map((a) => a.leave()),
      endpoint?.then((e) => e.close()),
    ]);
  }

  #discovered(peerId: string, adapter: SignalingAdapter): void {
    this.#heardFrom(peerId);
    const existing = this.#peers.get(peerId);
    if (existing && existing.state !== "failed" && existing.state !== "closed") return;
    // Recently failed: wait for its retry instead of reconnecting at once (and failing again).
    if ((this.#failures.get(peerId)?.retryAt ?? 0) > Date.now()) return;
    this.#create(peerId, adapter);
  }

  async #signal(from: string, payload: SignalPayload, adapter: SignalingAdapter): Promise<void> {
    this.#heardFrom(from);
    if (payload.kind === "full") {
      // Stop trying that peer: it won't answer.
      this.block(from);
      for (const listener of this.#fullListeners) listener(from, payload.maxPeers);
      return;
    }
    if (payload.kind === "relay") return this.#relayOffered(from, payload, adapter);
    // An offer can arrive before we've seen the sender's announce.
    const peer = this.#peers.get(from) ?? this.#create(from, adapter);
    await peer?.handleSignal(payload);
  }

  /** A peer gave up on connecting directly and told us where its relay is. */
  #relayOffered(
    from: string,
    payload: { endpointId: string; relayUrl: string },
    adapter: SignalingAdapter,
  ): void {
    if (!isEndpointId(payload.endpointId) || !isRelayUrl(payload.relayUrl)) return;
    if (this.#blocked.has(from) || !this.#options.iroh) return;
    const address = { endpointId: payload.endpointId, relayUrl: payload.relayUrl };
    this.#addresses.set(from, address);
    const existing = this.#peers.get(from);
    if (existing?.kind === "iroh") {
      existing.setRemote(address);
      return;
    }
    // We're connected directly after all? Then they'll see it too; keep that.
    if (existing?.state === "connected") return;
    this.#switchToRelay(from, adapter);
  }

  /** A failed peer that's still around isn't forgotten (see #failures). */
  #heardFrom(peerId: string): void {
    const failure = this.#failures.get(peerId);
    if (failure && failure.retryAt <= Date.now()) clearTimeout(failure.timer);
  }

  #forgetFailure(peerId: string): void {
    const failure = this.#failures.get(peerId);
    if (!failure) return;
    clearTimeout(failure.timer);
    this.#failures.delete(peerId);
    if (failure.answered >= 2) for (const listener of this.#unreachableListeners) listener();
  }

  /** Drops the current (direct) attempt and starts a relayed one. */
  #switchToRelay(peerId: string, adapter: SignalingAdapter): void {
    this.#relayed.add(peerId);
    const previous = this.#peers.get(peerId);
    if (previous) {
      this.#peers.delete(peerId);
      previous.close();
      for (const listener of this.#leftListeners) listener(peerId, "failed");
    }
    this.#create(peerId, adapter);
  }

  #create(peerId: string, adapter: SignalingAdapter): MeshPeer | undefined {
    if (!this.#running || peerId === this.selfId || this.#blocked.has(peerId)) return undefined;
    const live = [...this.#peers.values()].filter(
      (p) => p.state !== "closed" && p.state !== "failed",
    );
    if (live.length >= this.#options.maxPeers) {
      if (!this.#rejected.has(peerId)) {
        this.#rejected.add(peerId);
        for (const listener of this.#rejectedListeners) listener(peerId);
        // Tell them, so they can say so instead of waiting for an answer that never comes.
        void adapter
          .send(peerId, { kind: "full", maxPeers: this.#options.maxPeers })
          .catch(() => {});
      }
      return undefined;
    }
    this.#peers.get(peerId)?.close();
    this.#routes.set(peerId, this.#routes.get(peerId) ?? adapter);
    if (this.#relayed.has(peerId) && this.#options.iroh) {
      // Binding the endpoint is async (the first time loads iroh); the peer follows.
      void this.#createRelayed(peerId, adapter);
      return undefined;
    }
    const peer = new Peer({
      ...this.#options.peerOptions,
      selfId: this.selfId,
      peerId,
      ...(this.#options.rtcConfig ? { rtcConfig: this.#options.rtcConfig } : {}),
      sendSignal: (payload) => (this.#routes.get(peerId) ?? adapter).send(peerId, payload),
    });
    this.#wire(peer, adapter);
    return peer;
  }

  async #createRelayed(peerId: string, adapter: SignalingAdapter): Promise<void> {
    let endpoint: IrohEndpoint;
    try {
      endpoint = await this.#bindEndpoint();
    } catch {
      // No relay reachable: nothing to fall back to. Treat it like a failed attempt.
      this.#relayed.delete(peerId);
      this.#recordFailure(peerId, true, adapter);
      for (const listener of this.#leftListeners) listener(peerId, "failed");
      return;
    }
    if (!this.#running || !this.#relayed.has(peerId)) return;
    const existing = this.#peers.get(peerId);
    if (existing && existing.state !== "failed" && existing.state !== "closed") return;
    const peer = new IrohPeer({
      selfId: this.selfId,
      peerId,
      endpoint,
      ...(this.#options.peerOptions?.connectTimeoutMs
        ? { connectTimeoutMs: this.#options.peerOptions.connectTimeoutMs }
        : {}),
    });
    this.#wire(peer, adapter);
    // Tell them how to reach us, every attempt: the last one may have been missed.
    void (this.#routes.get(peerId) ?? adapter)
      .send(peerId, { kind: "relay", endpointId: endpoint.id, relayUrl: endpoint.relayUrl })
      .catch(() => {});
    const address = this.#addresses.get(peerId);
    if (address) peer.setRemote(address);
  }

  /** Binds this side's endpoint (once) and starts accepting connections on it. */
  #bindEndpoint(): Promise<IrohEndpoint> {
    const iroh = this.#options.iroh;
    if (!iroh) return Promise.reject(new Error("no relays configured"));
    if (!this.#endpoint) {
      const bound = iroh.bind(iroh.relayUrls);
      const binding = Promise.race([
        bound,
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error("no relay reachable")), BIND_TIMEOUT_MS),
        ),
      ]);
      // Timed out, then bound after all: nobody's waiting for it any more.
      binding.catch(() => void bound.then((endpoint) => endpoint.close()).catch(() => {}));
      this.#endpoint = binding;
      binding.then(
        (endpoint) => {
          if (this.#endpoint !== binding) return void endpoint.close();
          void this.#acceptConnections(endpoint);
        },
        () => {
          if (this.#endpoint === binding) this.#endpoint = undefined; // try again next time
        },
      );
    }
    return this.#endpoint;
  }

  async #acceptConnections(endpoint: IrohEndpoint): Promise<void> {
    for (;;) {
      let connection: Awaited<ReturnType<IrohEndpoint["accept"]>>;
      try {
        connection = await endpoint.accept();
      } catch {
        return;
      }
      if (!connection) return;
      // Only peers of this room, who told us their endpoint id, get through.
      const peerId = [...this.#addresses].find(
        ([, a]) => a.endpointId === connection.remoteId,
      )?.[0];
      if (!peerId || this.#blocked.has(peerId) || !this.#running) {
        connection.close(0, "unknown peer");
        continue;
      }
      let peer = this.#peers.get(peerId);
      if (peer?.kind !== "iroh") {
        // They dialed before we gave up on a direct connection: follow them.
        const adapter = this.#routes.get(peerId) ?? this.#options.adapters[0];
        if (!adapter) {
          connection.close(0, "no route");
          continue;
        }
        this.#relayed.add(peerId);
        if (peer) {
          this.#peers.delete(peerId);
          peer.close();
          for (const listener of this.#leftListeners) listener(peerId, "failed");
        }
        peer = new IrohPeer({
          selfId: this.selfId,
          peerId,
          endpoint,
          ...(this.#options.peerOptions?.connectTimeoutMs
            ? { connectTimeoutMs: this.#options.peerOptions.connectTimeoutMs }
            : {}),
        });
        this.#wire(peer, adapter);
      }
      peer.accept(connection);
    }
  }

  /** Bookkeeping for a failed attempt: when to retry, and whether the peer is unreachable. */
  #recordFailure(peerId: string, answered: boolean, adapter: SignalingAdapter): void {
    const previous = this.#failures.get(peerId);
    clearTimeout(previous?.timer);
    const count = (previous?.count ?? 0) + 1;
    const answeredCount = (previous?.answered ?? 0) + (answered ? 1 : 0);
    const delays = this.#options.retryDelaysMs ?? RETRY_DELAYS_MS;
    const delay = delays[Math.min(count, delays.length) - 1] ?? 0;
    const goneAfter = this.#options.goneAfterMs ?? GONE_AFTER_MS;
    const retry = () => {
      this.#rediscover(peerId, adapter);
      failure.timer = setTimeout(() => {
        if (this.#failures.get(peerId) === failure) this.#forgetFailure(peerId);
      }, goneAfter);
    };
    const failure = {
      count,
      answered: answeredCount,
      retryAt: Date.now() + delay,
      timer: setTimeout(retry, delay),
    };
    this.#failures.set(peerId, failure);
    if (answeredCount === 2 && answered)
      for (const listener of this.#unreachableListeners) listener();
  }

  /** Lets a peer be rediscovered: forget it, and nudge it in case it's still around. */
  #rediscover(peerId: string, adapter: SignalingAdapter): void {
    for (const a of this.#options.adapters) a.forget(peerId);
    if (this.#running) void adapter.send(peerId, { kind: "announce" }).catch(() => {});
  }

  #wire(peer: MeshPeer, adapter: SignalingAdapter): void {
    const peerId = peer.peerId;
    this.#peers.set(peerId, peer);
    const drop = (reason: LeaveReason) => {
      if (this.#peers.get(peerId) !== peer) return;
      this.#peers.delete(peerId);
      peer.close();
      if (reason === "failed") {
        const canRelay =
          peer.kind === "webrtc" &&
          peer.answered &&
          this.#options.iroh !== undefined &&
          !this.#relayed.has(peerId);
        if (canRelay) {
          // They're there, but no direct path: go through a relay, right away.
          this.#recordFailure(peerId, true, adapter);
          const failure = this.#failures.get(peerId);
          if (failure) {
            clearTimeout(failure.timer);
            failure.retryAt = 0;
          }
          for (const listener of this.#leftListeners) listener(peerId, reason);
          this.#switchToRelay(peerId, adapter);
          return;
        }
        // Gone, or unreachable from here: try again later, less often each time.
        this.#recordFailure(peerId, peer.answered, adapter);
      }
      for (const listener of this.#leftListeners) listener(peerId, reason);
      if (reason === "closed") this.#rediscover(peerId, adapter);
    };
    peer.onStateChange((state) => {
      if (state === "connected") this.#forgetFailure(peerId);
      if (state === "failed") drop("failed");
      if (state === "closed") drop("closed");
    });
    // The remote closing its connection shows up first as the control channel closing.
    peer.control.onClose(() => drop("closed"));

    for (const listener of this.#peerListeners) listener(peer);
  }
}
